import React, {useContext, useEffect, useState} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  View,
} from 'react-native';
import {SafeAreaView} from 'react-native-safe-area-context';

import Button from '../../components/elements/Button';
import Header from '../../components/elements/Header';
import Divider from '../../components/elements/Divider';
import Text from '../../components/elements/Text';

import makeStyles from './styles';
import {useTheme, useThemedStyles} from '../../theme';
import {BUTTON_TYPES, KEYS} from '../../utils/Constants';
import Input from '../../components/elements/Input';
import {
  getData,
  getPatientCondition,
  getUserProfileData,
  storeUserProfileData,
} from '../../utils/GeneralStorage';
import {fetchApi} from '../../utils/ApiHelper';
import {HTTP_METHODS, POST_DIABETES_TYPE} from '../../utils/URLConstants';
import {DLog} from '../../utils/HelperMethods';
import {loadingPauseContext} from '../../contexts/LoadingPauseContext';
import {errorStatusContext} from '../../contexts/ErrorStatusContext';
import CommonStyles from '../../utils/CommonStyles';
import {tID} from '../../utils/TestIdHelper';
import KeyboardDoneBar from '../../components/elements/KeyboardDoneBar';
import {CloseIcon, LeftArrowIcon, LockIcon} from '../../utils/SvgIcons';

const BloodSugarRange = ({route, navigation, onClose = () => {}}) => {
  const [hyperValue, setHyperValue] = useState('');
  const [hyperError, setHyperError] = useState(false);
  const [hypoValue, setHypoValue] = useState('');
  const [hypoError, setHypoError] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);

  const lpc = useContext(loadingPauseContext);
  const esc = useContext(errorStatusContext);

  useEffect(() => {
    async function getCondition() {
      const patientCondition = await getPatientCondition();
      if (patientCondition) {
        setHypoValue(patientCondition.minValue);
        setHyperValue(patientCondition.maxValue);
      }
    }
    getCondition();
  }, []);

  const updateRange = async response => {
    const profile = await getUserProfileData();
    let userProfile = {...profile};
    const existing = userProfile.patientCondition?.[0] || {};
    const condition = {
      ...existing,
      minValue: response.minValue,
      maxValue: response.maxValue,
    };
    userProfile.patientCondition = [condition];
    storeUserProfileData(userProfile);
  };

  const isValidBSValues = () => {
    setHypoError(false);
    setHyperError(false);
    setErrorMsg('');

    let numHypo = parseInt(hypoValue);
    let numHyper = parseInt(hyperValue);

    // DLog(numHypo, numHyper, hypoValue, hyperValue);

    if ('' + numHypo !== hypoValue) {
      setHypoError(true);
      setErrorMsg('Please enter numeric values');
      return false;
    }
    if ('' + numHyper !== hyperValue) {
      setHyperError(true);
      setErrorMsg('Please enter numeric values');
      return false;
    }
    if (numHypo <= 0) {
      setHypoError(true);
      setErrorMsg(`Values must be >0`);
      return false;
    }
    if (numHyper <= 0) {
      setHyperError(true);
      setErrorMsg(`Values must be >0`);
      return false;
    }
    if (numHypo > 400) {
      setHypoError(true);
      setErrorMsg(`Values must be <400`);
      return false;
    }
    if (numHyper > 400) {
      setHyperError(true);
      setErrorMsg(`Values must be <400`);
      return false;
    }
    if (numHypo >= numHyper) {
      setHypoError(true);
      setHyperError(true);
      setErrorMsg(`Hypoglycemic value must be less than Hyperglycemic value`);
      return false;
    }

    return true;
  };

  const onPressSave = async () => {
    if (isValidBSValues()) {
      const userId = await getData(KEYS.userId);
      const postParams = {
        patientId: parseInt(userId),
        minValue: parseInt(hypoValue),
        maxValue: parseInt(hyperValue),
      };
      lpc.setVisible(true);

      fetchApi(HTTP_METHODS.PUT, POST_DIABETES_TYPE, postParams)
        .then(response => {
          lpc.setVisible(false);
          if (response.status === 200) {
            updateRange(response.data.data[0]);
            if (Platform.OS === 'web') {
              onClose();
            } else {
              navigation.goBack();
            }
          }
        })
        .catch(error => {
          // DLog(error);
          lpc.setVisible(false);
          esc.showError(error);
        });
    }
  };

  const renderSectionHeader = (indicatorColor, title) => {
    return (
      <View style={styles.sectionHeaderContaier}>
        <View style={styles.dotView(indicatorColor)} />
        <Text
          testID={tID(`BloodSugarRange-Text-${title}`)}
          variation="Body1SemiBold"
          color={colors.textMuted}>
          {title}
        </Text>
      </View>
    );
  };

  const renderRangeText = text => {
    return (
      <View style={styles.rangeTextContainer}>
        <Text variation={'Body1Regular'} color={colors.textPrimary}>
          {text}
        </Text>
        <LockIcon />
      </View>
    );
  };

  return (
    <SafeAreaView
      testID={tID('BloodSugarRange-SafeAreaView-Main')}
      style={styles.container}>
      <View
        testID={tID('BloodSugarRange-View-HeaderContainer')}
        style={[styles.headerView, {marginTop: 30}]}>
        <Header
          title="My Blood Sugar Range"
          onPressLeftIcon={() => {
            if (Platform.OS === 'web') {
              onClose();
            } else {
              navigation.goBack();
              route?.params?.onRequestOpen && route?.params?.onRequestOpen();
            }
          }}
          leftImage={Platform.OS === 'web' ? <CloseIcon /> : <LeftArrowIcon />}
        />
      </View>
      <KeyboardAvoidingView
        testID={tID('BloodSugarRange-KeyboardAvoidingView-1')}
        behavior={Platform.OS === 'ios' ? 'padding' : null}
        style={styles.container}
        // keyboardVerticalOffset={Platform.OS === "ios" ? 64 : 0}
      >
        <ScrollView keyboardShouldPersistTaps="handled"
          testID={tID('BloodSugarRange-ScrollView-1')}
          style={styles.scrollView}
          contentContainerStyle={styles.scrollviewContainer}>
          <Text variation="Body1Regular" topMargin={24} color={colors.textSecondary}>
            Customize your blood sugar range to see whether readings are Low,
            In Range, or High.
          </Text>
          <Divider top={24} />
          {renderSectionHeader(colors.warning, 'High')}
          {renderRangeText(
            <>
              Above{' '}
              <Text
                testID={tID('BloodSugarRange-Text-Hyper')}
                variation="Body1SemiBold"
                color={colors.textPrimary}>
                {hyperValue ? hyperValue - -1 : hyperValue}
              </Text>{' '}
              mg/dL
            </>,
          )}
          {renderSectionHeader(colors.success, 'In Range')}
          <View style={styles.inputContainer}>
            <Input
              testID={tID('BloodSugarRange-Input-Hypo')}
              hasError={hypoError}
              containerStyle={styles.container}
              inputStyle={[
                styles.inputStyle,
                Platform.OS === 'web' && CommonStyles.full_width,
              ]}
              rightText={'mg/dL'}
              maxLength={3}
              value={hypoValue}
              onChangeText={setHypoValue}
              keyboardType={'number-pad'}
              inputAccessoryViewID="bsRangeDone"
            />
            <Text
              variation="Body1SemiBold"
              color={colors.textPrimary}
              style={styles.marginHorizontal16}>
              To
            </Text>
            <Input
              testID={tID('BloodSugarRange-Input-Hyper')}
              hasError={hyperError}
              containerStyle={styles.container}
              inputStyle={[
                styles.inputStyle,
                Platform.OS === 'web' && CommonStyles.full_width,
              ]}
              rightText={'mg/dL'}
              maxLength={3}
              value={hyperValue}
              onChangeText={setHyperValue}
              keyboardType={'number-pad'}
              inputAccessoryViewID="bsRangeDone"
            />
          </View>
          {errorMsg ? (
            <Text
              testID={tID('BloodSugarRange-Text-InputError')}
              variation="Body1Regular"
              topMargin={8}
              color={colors.danger}>
              {errorMsg}
            </Text>
          ) : null}
          {renderSectionHeader(colors.accent, 'Low')}
          {renderRangeText(
            <>
              Below{' '}
              <Text
                testID={tID('BloodSugarRange-Text-Hypo')}
                variation="Body1SemiBold"
                color={colors.textPrimary}>
                {hypoValue ? hypoValue - 1 : hypoValue}
              </Text>{' '}
              mg/dL
            </>,
          )}
        </ScrollView>
      </KeyboardAvoidingView>
      <KeyboardDoneBar nativeID="bsRangeDone" />
      <View style={[styles.buttonContainer, {paddingBottom: 24}]}>
        <Button
          testID={tID('BloodSugarRange-Button-Save')}
          // disabled={!isValidForm}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={colors.textOnAccent}
          title={'Save'}
          onPress={onPressSave}
        />
      </View>
    </SafeAreaView>
  );
};

export default BloodSugarRange;
